import React, { useCallback, useEffect, useMemo } from "react";
import AbcIcon from "@mui/icons-material/Abc";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import CycloneIcon from "@mui/icons-material/Cyclone";
import ExtensionIcon from "@mui/icons-material/Extension";
import GridOnIcon from "@mui/icons-material/GridOn";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { Link as RouterLink } from "react-router-dom";
import useTutorEnable from "../../hooks/useTutorEnable";
import { trackEvent } from "../../services/GoogleAnalytics";
import { getContentLanguage } from "../../services/LangPrefs";
import { usePagesTranslation } from "../locales";
import LandingDailyPlaySection from "./LandingDailyPlaySection";
import LandingEducatorSection from "./LandingEducatorSection";
import LandingAboutUsSection from "./LandingAboutUsSection";
import LandingLatestStoriesSection from "./LandingLatestStoriesSection";
import "./landingAppFirst.css";

const IMAGE_GAMES = [
    {
        to: "/puzzles/jigsaw",
        icon: ExtensionIcon,
        accent: "#d8b4fe",
        wash: "rgba(168, 85, 247, 0.18)",
        scene: "jigsaw",
        nameKey: "landingApp.hero-pick-jigsaw",
        name: "Jigsaw",
        blurbKey: "landingApp.hero-jigsaw-blurb",
        blurb: "Assemble detailed images piece by piece.",
        benefitKey: "landingApp.hero-jigsaw-benefit",
        benefit: "Enhances visual memory, spatial reasoning, and mindfulness.",
    },
    {
        to: "/puzzles/sliding",
        icon: ViewModuleIcon,
        accent: "#fdba74",
        wash: "rgba(249, 115, 22, 0.18)",
        scene: "sliding",
        nameKey: "landingApp.hero-pick-sliding",
        name: "Sliding",
        blurbKey: "landingApp.hero-sliding-blurb",
        blurb: "Slide numbered or visual tiles into order within minimal moves.",
        benefitKey: "landingApp.hero-sliding-benefit",
        benefit: "Builds sequential planning, problem-solving, and spatial awareness.",
    },
];

const WORD_GAMES = [
    {
        to: "/puzzles/word-wheel",
        icon: CycloneIcon,
        accent: "#fcd34d",
        wash: "rgba(245, 158, 11, 0.18)",
        scene: "wheel",
        nameKey: "landingApp.hero-pick-word-wheel",
        name: "Word Wheel Quest",
        blurbKey: "landingApp.hero-word-wheel-blurb",
        blurb: "Unscramble letters from a circular wheel to discover hidden words.",
        benefitKey: "landingApp.hero-word-wheel-benefit",
        benefit: "Trains fast anagramming, word association, and vocabulary recall.",
    },
    {
        to: "/puzzles/crossword",
        icon: GridOnIcon,
        accent: "#93c5fd",
        wash: "rgba(59, 130, 246, 0.18)",
        scene: "crossword",
        nameKey: "landingApp.hero-pick-crossword",
        name: "Crossword",
        blurbKey: "landingApp.hero-crossword-blurb",
        blurb: "Solve clues to complete intersecting word grids.",
        benefitKey: "landingApp.hero-crossword-benefit",
        benefit: "Expands general knowledge, lateral thinking, and verbal memory.",
    },
    {
        to: "/puzzles/hidden-message",
        icon: MessageOutlinedIcon,
        accent: "#7dd3fc",
        wash: "rgba(14, 165, 233, 0.18)",
        scene: "hidden",
        nameKey: "landingApp.hero-pick-hidden",
        name: "Hidden Message",
        blurbKey: "landingApp.hero-hidden-blurb",
        blurb: "Solve word targets to reveal hidden quotes or phrases.",
        benefitKey: "landingApp.hero-hidden-benefit",
        benefit: "Combines logic deduction with satisfying narrative discovery.",
    },
    {
        to: "/puzzles/word-search",
        icon: SearchIcon,
        accent: "#6ee7a8",
        wash: "rgba(59, 166, 102, 0.2)",
        scene: "search",
        nameKey: "landingApp.hero-pick-word-search",
        name: "Word Search",
        blurbKey: "landingApp.hero-word-search-blurb",
        blurb: "Scan letter grids to locate themed target words.",
        benefitKey: "landingApp.hero-word-search-benefit",
        benefit: "Sharpens visual scanning, attention to detail, and pattern recognition.",
    },
];

function GameCard({ t, game }) {
    const Icon = game.icon;
    return (
        <RouterLink className={`pi-game-card pi-game-card--${game.scene}`} to={game.to}>
            <img className="pi-game-card__art" src={`/landing/${game.scene}.jpg`} alt="" />
            <span className="pi-game-card__veil" aria-hidden="true" />
            <span className="pi-game-card__name">{t(game.nameKey, game.name)}</span>
            <span className="pi-game-card__foot">
                <span
                    className="pi-game-card__icon"
                    style={{ color: game.accent, background: game.wash }}
                    aria-hidden="true"
                >
                    <Icon />
                </span>
                <span className="pi-game-card__blurb">{t(game.blurbKey, game.blurb)}</span>
            </span>
        </RouterLink>
    );
}

/**
 * Public landing page for "/": play catalog above the daily free web tiers.
 * The previous editorial landing still lives at "/landing".
 */
export default function LandingAppFirstPage() {
    const { t } = usePagesTranslation();
    const contentLanguage = useMemo(() => getContentLanguage(), []);
    const { tutorEnable } = useTutorEnable();

    useEffect(() => {
        trackEvent("entered", { name: "LandingAppFirst" });
    }, []);

    const scrollToDailyPlay = useCallback((event) => {
        event.preventDefault();
        const target = document.getElementById("daily-free-play");
        if (!target) return;
        const reduceMotion =
            typeof window !== "undefined" &&
            window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        target.scrollIntoView({
            behavior: reduceMotion ? "auto" : "smooth",
            block: "start",
        });
        trackEvent("clicked", { name: "LandingPlayFreeWebTiers" });
    }, []);

    return (
        <div className="pi-landing">
            <div className="pi-landing__inner">
                <section className="pi-hero" aria-labelledby="pi-hero-title">
                    <div className="pi-hero__copy">
                        <div className="pi-hero__head">
                            <div className="pi-hero__lead">
                        <h1 className="pi-hero__title" id="pi-hero-title">
                            {t("landingApp.hero-title-lead", "Play Interactive Puzzles Online")}
                        </h1>
                        <p className="pi-hero__tagline">
                            {t(
                                "landingApp.hero-tagline",
                                "Explore engaging image and word puzzles designed to sharpen your mind, boost concentration, and provide instant casual fun directly in your browser.",
                            )}
                        </p>
                        <RouterLink
                            className="pi-hero__start"
                            to="/puzzles/jigsaw"
                            onClick={() => trackEvent("clicked", { name: "LandingStartSolvingNow" })}
                        >
                            {t("landingApp.hero-start-cta", "Start Solving Now")}
                        </RouterLink>
                            </div>
                            <div className="pi-hero__aside">
                                <span className="pi-hero__glow" aria-hidden="true">
                                    <img src="/landing/hero-tree.jpg" alt="" />
                                </span>
                            </div>
                        </div>
                        <div className="pi-catalog">
                            <section className="pi-catalog__group" aria-labelledby="pi-image-puzzles">
                                <h2 className="pi-catalog__title" id="pi-image-puzzles">
                                    <span className="pi-catalog__title-icon" aria-hidden="true">
                                        <ImageOutlinedIcon />
                                    </span>
                                    {t("landingApp.hero-image-title", "Image Puzzles")}
                                </h2>
                                <p className="pi-catalog__intro">
                                    {t(
                                        "landingApp.hero-image-blurb",
                                        "Visual-spatial challenges built for focus, relaxation, and spatial logic.",
                                    )}
                                </p>
                                <div className="pi-catalog__grid">
                                    {IMAGE_GAMES.map((game) => (
                                        <GameCard key={game.to} t={t} game={game} />
                                    ))}
                                </div>
                            </section>
                            <section className="pi-catalog__group" aria-labelledby="pi-word-puzzles">
                                <h2 className="pi-catalog__title" id="pi-word-puzzles">
                                    <span className="pi-catalog__title-icon" aria-hidden="true">
                                        <AbcIcon />
                                    </span>
                                    {t("landingApp.hero-word-title", "Word Puzzles")}
                                </h2>
                                <p className="pi-catalog__intro">
                                    {t(
                                        "landingApp.hero-word-blurb",
                                        "Vocabulary-driven puzzles crafted to exercise language recall and deduction.",
                                    )}
                                </p>
                                <div className="pi-catalog__grid">
                                    {WORD_GAMES.map((game) => (
                                        <GameCard key={game.to} t={t} game={game} />
                                    ))}
                                </div>
                            </section>
                        </div>
                        <RouterLink
                                className="pi-create-callout"
                                to="/tutors"
                                onClick={() => trackEvent("clicked", { name: "LandingTryCreatePuzzle" })}
                            >
                                <span className="pi-create-callout__badge">
                                    <span className="pi-create-callout__icon" aria-hidden>
                                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                                            <path d="M12 2.2 13.6 8 19.4 9.6 13.6 11.2 12 17 10.4 11.2 4.6 9.6 10.4 8 12 2.2Z" />
                                            <path d="M18.2 13.2 19 15.4 21.2 16.2 19 17 18.2 19.2 17.4 17 15.2 16.2 17.4 15.4 18.2 13.2Z" />
                                        </svg>
                                    </span>
                                    {t("landingApp.create-try-kicker", "Custom Creator Tool")}
                                </span>
                                <span className="pi-create-callout__title">
                                    {t("landingApp.create-try-title", "Create Your Own Crossword")}
                                </span>
                                <span className="pi-create-callout__hook">
                                    {t(
                                        "landingApp.create-try-hook",
                                        "Turn lessons or custom text into crosswords in seconds.",
                                    )}
                                </span>
                                <span
                                    className="pi-create-callout__steps"
                                    aria-label={t(
                                        "landingApp.create-try-steps-label",
                                        "Paste text, generate, then print or share",
                                    )}
                                >
                                    <span className="pi-create-callout__step">
                                        <ContentPasteIcon aria-hidden="true" />
                                        {t("landingApp.create-step-paste", "Paste text")}
                                    </span>
                                    <span className="pi-create-callout__arrow" aria-hidden="true">→</span>
                                    <span className="pi-create-callout__step">
                                        <AutoAwesomeIcon aria-hidden="true" />
                                        {t("landingApp.create-step-generate", "Generate")}
                                    </span>
                                    <span className="pi-create-callout__arrow" aria-hidden="true">→</span>
                                    <span className="pi-create-callout__step">
                                        <PrintIcon aria-hidden="true" />
                                        {t("landingApp.create-step-share", "Print/Share")}
                                    </span>
                                </span>
                                <span className="pi-create-callout__body">
                                    {t(
                                        "landingApp.create-try-privacy",
                                        "Your content stays private to your account.",
                                    )}
                                </span>
                            </RouterLink>
                        <div className="pi-hero__actions">
                            <a
                                className="pi-hero__scroll-link"
                                href="#daily-free-play"
                                onClick={scrollToDailyPlay}
                            >
                                {t("landingApp.hero-secondary-cta", "Play Free Web Tiers")}
                            </a>
                        </div>
                    </div>
                </section>
            </div>

            <LandingDailyPlaySection contentLanguage={contentLanguage} />
            <LandingLatestStoriesSection />
            {tutorEnable ? <LandingEducatorSection /> : null}
            <LandingAboutUsSection />
        </div>
    );
}
